import { collection, doc, getDoc, getDocs, onSnapshot, setDoc, updateDoc, deleteDoc, deleteField, query, where, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { POSUser } from "../components/type/POSUser";
import { getDefaultPermissionsForRole } from "./permissions/posUserDefaultPermissions";

// IndexedDB storage for offline support
const DB_NAME = "POSUsersDB";
const STORE_NAME = "posUsers";
const DB_VERSION = 1;

let dbInstance: IDBDatabase | null = null;

// Initialize IndexedDB
const initIndexedDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error("Failed to open IndexedDB"));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("storeId", "storeId", { unique: false });
        store.createIndex("pinHash", "pinHash", { unique: false });
      }
    };
  });
};

// Hash PIN using Web Crypto API (SHA256)
export const hashPIN = async (pin: string): Promise<string> => {
  // Ensure PIN is exactly 6 digits
  if (!/^\d{6}$/.test(pin)) {
    throw new Error("PIN must be exactly 6 digits");
  }

  // Use Web Crypto API for hashing
  const encoder = new TextEncoder();
  const data = encoder.encode(pin);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

// Synchronous version using simple hash (for compatibility)
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Convert to positive hex string
  return Math.abs(hash).toString(16).padStart(8, '0').repeat(4).substring(0, 64);
};

// Synchronous hash for PIN (fallback)
export const hashPINSync = (pin: string): string => {
  if (!/^\d{6}$/.test(pin)) {
    throw new Error("PIN must be exactly 6 digits");
  }
  // Use simple hash for synchronous operations
  return simpleHash(pin);
};

// Compare PIN with hash (async)
export const comparePIN = async (pin: string, hash: string): Promise<boolean> => {
  try {
    const inputHash = await hashPIN(pin);
    return inputHash === hash;
  } catch (error) {
    return false;
  }
};

// Compare PIN with hash (sync - for IndexedDB)
export const comparePINSync = (pin: string, hash: string): boolean => {
  try {
    const inputHash = hashPINSync(pin);
    return inputHash === hash;
  } catch (error) {
    return false;
  }
};

// Get POS users from Firestore
export const getPOSUsers = async (storeId: string, forceRefresh: boolean = false): Promise<POSUser[]> => {
  // If not forcing refresh, try IndexedDB first (fast, offline-first)
  if (!forceRefresh) {
    try {
      const cachedUsers = await getPOSUsersFromIndexedDB(storeId);
      // If we have cached users, return them immediately (fast path)
      if (cachedUsers.length > 0) {
        // Fetch from Firestore in background to update cache (non-blocking)
        fetchPOSUsersFromFirestore(storeId).catch((error) => {
          console.error("Background sync failed:", error);
        });
        return cachedUsers;
      }
    } catch (error) {
      console.error("Error reading from IndexedDB:", error);
    }
  }

  // If IndexedDB is empty or force refresh, fetch from Firestore
  return fetchPOSUsersFromFirestore(storeId);
};

// Fetch POS users from Firestore (separate function for clarity)
const fetchPOSUsersFromFirestore = async (storeId: string): Promise<POSUser[]> => {
  try {
    const posUsersRef = collection(db, "stores", storeId, "posUsers");
    const snapshot = await getDocs(posUsersRef);
    const users: POSUser[] = [];

    snapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data(),
      } as POSUser);
    });

    try {
      await cachePOSUsersToIndexedDB(storeId, users);
    } catch (cacheError) {
      console.error("Error caching to IndexedDB:", cacheError);
    }

    return users;
  } catch (error) {
    console.error("Error fetching POS users from Firestore:", error);
    // Fallback to IndexedDB if offline
    return getPOSUsersFromIndexedDB(storeId);
  }
};

// Get POS users from IndexedDB (offline)
const getPOSUsersFromIndexedDB = async (storeId: string): Promise<POSUser[]> => {
  try {
    const db = await initIndexedDB();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index("storeId");
    const request = index.getAll(storeId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(request.result || []);
      };
      request.onerror = () => {
        reject(new Error("Failed to read from IndexedDB"));
      };
    });
  } catch (error) {
    console.error("Error reading POS users from IndexedDB:", error);
    return [];
  }
};

// Cache POS users to IndexedDB. Resolves after the store's full list is replaced.
const cachePOSUsersToIndexedDB = async (storeId: string, users: POSUser[]): Promise<void> => {
  const db = await initIndexedDB();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index("storeId");
    const clearRequest = index.openKeyCursor(IDBKeyRange.only(storeId));

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error("Failed to cache POS users"));
    transaction.onabort = () => reject(transaction.error || new Error("Failed to cache POS users"));
    clearRequest.onerror = () => reject(clearRequest.error || new Error("Failed to cache POS users"));

    clearRequest.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      } else {
        users.forEach((user) => {
          store.put({ ...user, storeId });
        });
      }
    };
  });
};

const normalizePOSUserName = (name: string): string => name.trim().replace(/\s+/g, " ").toLowerCase();

const posUserUpdatedAt = (user: POSUser): number => {
  const stamp = user.updatedAt as Timestamp | undefined;
  if (stamp && typeof stamp.toMillis === "function") return stamp.toMillis();
  const seconds = (stamp as { seconds?: number } | undefined)?.seconds;
  return typeof seconds === "number" ? seconds * 1000 : 0;
};

const comparePOSUserKeeper = (a: POSUser, b: POSUser): number => {
  const rank = (user: POSUser) => {
    if (user.mevVerified && user.isActive !== false) return 0;
    if (user.isActive !== false) return 1;
    return 2;
  };
  const byRank = rank(a) - rank(b);
  if (byRank !== 0) return byRank;
  const byTime = posUserUpdatedAt(b) - posUserUpdatedAt(a);
  if (byTime !== 0) return byTime;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
};

const keeperForName = (matches: POSUser[]): POSUser =>
  [...matches].sort(comparePOSUserKeeper)[0];

// Same display name keeps one record. Prefer an active MEV-verified row, then the latest update.
const pickExistingPOSUser = (users: POSUser[], name: string): POSUser | undefined => {
  const key = normalizePOSUserName(name);
  if (!key) return undefined;
  const matches = users.filter((user) => normalizePOSUserName(user.name || "") === key);
  if (matches.length === 0) return undefined;
  return keeperForName(matches);
};

// Delete extra rows that share a display name. The remaining POS user stays active.
export const removeDuplicatePOSUsers = async (storeId: string, users: POSUser[]): Promise<POSUser[]> => {
  const groups = new Map<string, POSUser[]>();
  users.forEach((user) => {
    const key = normalizePOSUserName(user.name || "");
    if (!key) return;
    const group = groups.get(key) || [];
    group.push(user);
    groups.set(key, group);
  });

  const removeIds = new Set<string>();
  const activateIds = new Set<string>();
  groups.forEach((matches) => {
    if (matches.length < 2) return;
    const keeper = keeperForName(matches);
    matches.forEach((user) => {
      if (user.id !== keeper.id) removeIds.add(user.id);
    });
    if (keeper.isActive === false) activateIds.add(keeper.id);
  });

  if (removeIds.size === 0 && activateIds.size === 0) return users;

  try {
    await Promise.all(Array.from(removeIds).map((userId) =>
      deleteDoc(doc(db, "stores", storeId, "posUsers", userId))
    ));
    await Promise.all(Array.from(activateIds).map((userId) =>
      updateDoc(doc(db, "stores", storeId, "posUsers", userId), {
        isActive: true,
        updatedAt: serverTimestamp(),
      })
    ));
  } catch (error) {
    console.error("Error removing duplicate POS users:", error);
    return users;
  }

  const remaining = users
    .filter((user) => !removeIds.has(user.id))
    .map((user) => (activateIds.has(user.id) ? { ...user, isActive: true } : user));

  try {
    await cachePOSUsersToIndexedDB(storeId, remaining);
  } catch (error) {
    console.error("Error caching POS users after duplicate removal:", error);
  }

  return remaining;
};

export const POS_USERS_UPDATED_EVENT = "pos-users-updated";

// Live store list for a connected desktop. The callback runs after IndexedDB has the snapshot.
export const subscribeStorePOSUsers = (
  storeId: string,
  onUsers: (users: POSUser[]) => void
): (() => void) => {
  const posUsersRef = collection(db, "stores", storeId, "posUsers");
  return onSnapshot(posUsersRef, (snapshot) => {
    const users: POSUser[] = snapshot.docs.map((userDoc) => ({
      id: userDoc.id,
      ...userDoc.data(),
    } as POSUser));
    // An empty local cache event must not wipe a PIN list the register already has.
    if (snapshot.metadata.fromCache && users.length === 0) return;
    const applyUsers = snapshot.metadata.fromCache
      ? Promise.resolve(users)
      : removeDuplicatePOSUsers(storeId, users);
    applyUsers
      .then((uniqueUsers) => cachePOSUsersToIndexedDB(storeId, uniqueUsers).then(() => uniqueUsers))
      .then((uniqueUsers) => onUsers(uniqueUsers))
      .catch((error) => {
        console.error("Error caching POS users from listener:", error);
      });
  }, (error) => {
    console.error("POS user listener failed:", error);
  });
};

// Check if PIN is already in use by another user
export const isPINInUse = async (storeId: string, pin: string, excludeUserId?: string): Promise<boolean> => {
  const users = await getPOSUsers(storeId);

  // Hash the PIN to compare
  let pinHash: string;
  try {
    pinHash = await hashPIN(pin);
  } catch (error) {
    pinHash = hashPINSync(pin);
  }

  // Check if any user (except the excluded one) has this PIN hash
  return users.some(user => {
    if (excludeUserId && user.id === excludeUserId) {
      return false; // Skip the excluded user
    }
    return user.pinHash === pinHash;
  });
};

// Generate a random 6-digit PIN
export const generateRandomPIN = (): string => {
  // Generate a random number between 100000 and 999999
  return Math.floor(100000 + Math.random() * 900000).toString();
};

// Generate a unique PIN that's not already in use
export const generateUniquePIN = async (storeId: string, maxAttempts: number = 100): Promise<string> => {
  for (let i = 0; i < maxAttempts; i++) {
    const pin = generateRandomPIN();
    const isUsed = await isPINInUse(storeId, pin);
    if (!isUsed) {
      return pin;
    }
  }
  throw new Error("Unable to generate unique PIN after multiple attempts");
};

// Find POS user by PIN (optimized for speed - checks IndexedDB first)
export const findPOSUserByPIN = async (storeId: string, pin: string): Promise<POSUser | null> => {
  // First, try IndexedDB (fast, offline-first)
  try {
    const users = await getPOSUsersFromIndexedDB(storeId);

    // Compare with plain text PIN directly (we store it for fast lookup)
    const user = users.find((u) => {
      // Direct plain text comparison (fastest)
      if (u.pin === pin && u.isActive) {
        return true;
      }
      // Fallback to hash comparison for backwards compatibility
      if (u.pinHash) {
        return comparePINSync(pin, u.pinHash) && u.isActive;
      }
      return false;
    });

    if (user) {
      return user;
    }
  } catch (error) {
    console.error("Error reading from IndexedDB:", error);
  }

  // If not found in IndexedDB, try Firestore (but only if IndexedDB was empty)
  // This handles the case where IndexedDB hasn't been populated yet
  try {
    const users = await getPOSUsers(storeId);

    // Compare with plain text PIN directly
    const user = users.find((u) => {
      // Direct plain text comparison (fastest)
      if (u.pin === pin && u.isActive) {
        return true;
      }
      // Fallback to hash comparison for backwards compatibility
      if (u.pinHash) {
        return comparePINSync(pin, u.pinHash) && u.isActive;
      }
      return false;
    });

    return user || null;
  } catch (error) {
    console.error("Error finding POS user by PIN:", error);
    return null;
  }
};

// Create POS user. The same display name updates the existing record.
export const createPOSUser = async (
  storeId: string,
  name: string,
  pin: string,
  role: string,
  createdBy: string,
  mevVerified?: boolean
): Promise<POSUser> => {
  const existingUsers = await removeDuplicatePOSUsers(storeId, await getPOSUsers(storeId, true));
  const existing = pickExistingPOSUser(existingUsers, name);

  const pinInUse = await isPINInUse(storeId, pin, existing?.id);
  const keepCurrentPin = Boolean(existing && pinInUse && mevVerified === true);
  if (pinInUse && !keepCurrentPin) {
    throw new Error("PIN is already in use by another user");
  }

  // Hash PIN (try async first, fallback to sync)
  let pinHash: string;
  try {
    pinHash = await hashPIN(pin);
  } catch (error) {
    pinHash = hashPINSync(pin);
  }

  const defaultPermissions = getDefaultPermissionsForRole(role);
  const now = serverTimestamp();

  if (existing) {
    const userRef = doc(db, "stores", storeId, "posUsers", existing.id);
    const nextRole = (mevVerified === true && existing.role ? existing.role : role) as POSUser["role"];
    const updateData: any = {
      name,
      role: nextRole,
      isActive: true,
      updatedAt: now,
    };
    if (!keepCurrentPin) {
      updateData.pinHash = pinHash;
      updateData.pin = pin;
    }
    if (nextRole !== existing.role) {
      updateData.permissions = defaultPermissions;
    }
    if (mevVerified === true) {
      updateData.mevVerified = true;
    }
    await updateDoc(userRef, updateData);

    const refreshed = await getPOSUsers(storeId, true);
    const updated = refreshed.find((user) => user.id === existing.id);
    if (updated) return updated;

    return {
      ...existing,
      name,
      pinHash: keepCurrentPin ? existing.pinHash : pinHash,
      pin: keepCurrentPin ? existing.pin : pin,
      role: nextRole,
      permissions: nextRole !== existing.role ? defaultPermissions : existing.permissions,
      isActive: true,
      mevVerified: mevVerified === true ? true : existing.mevVerified,
      updatedAt: Timestamp.now(),
    };
  }

  const posUserRef = doc(collection(db, "stores", storeId, "posUsers"));

  const newUser: Omit<POSUser, "id"> = {
    name,
    pinHash,
    pin: pin, // Store plain text PIN for retrieval
    role: role as POSUser["role"],
    permissions: defaultPermissions, // Set default permissions based on role
    isActive: true,
    createdBy,
    createdAt: now as Timestamp,
    updatedAt: now as Timestamp,
  };
  if (mevVerified === true) {
    newUser.mevVerified = true;
  }

  await setDoc(posUserRef, newUser);

  const refreshed = await getPOSUsers(storeId, true);
  const created = refreshed.find((user) => user.id === posUserRef.id);
  if (created) return created;

  return {
    id: posUserRef.id,
    ...newUser,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
};

// Update POS user
export const updatePOSUser = async (
  storeId: string,
  userId: string,
  updates: Partial<Pick<POSUser, "name" | "pinHash" | "pin" | "role" | "permissions" | "isActive" | "stationIds" | "hourlyRateCents">>
): Promise<void> => {
  const userRef = doc(db, "stores", storeId, "posUsers", userId);
  const updateData: any = {
    ...updates,
    updatedAt: serverTimestamp(),
  };

  // If PIN is being updated, hash it and store plain text
  if (updates.pinHash && !updates.pinHash.startsWith("$2")) {
    // Check if it's a plain PIN (6 digits) or already hashed
    if (/^\d{6}$/.test(updates.pinHash)) {
      // Check if PIN is already in use by another user
      const pinInUse = await isPINInUse(storeId, updates.pinHash, userId);
      if (pinInUse) {
        throw new Error("PIN is already in use by another user");
      }

      try {
        updateData.pinHash = await hashPIN(updates.pinHash);
      } catch (error) {
        updateData.pinHash = hashPINSync(updates.pinHash);
      }
      // Store plain text PIN
      updateData.pin = updates.pinHash;
    }
  } else if (updates.pin && /^\d{6}$/.test(updates.pin)) {
    // If pin field is updated directly
    const pinInUse = await isPINInUse(storeId, updates.pin, userId);
    if (pinInUse) {
      throw new Error("PIN is already in use by another user");
    }

    try {
      updateData.pinHash = await hashPIN(updates.pin);
    } catch (error) {
      updateData.pinHash = hashPINSync(updates.pin);
    }
    updateData.pin = updates.pin;
  }

  // If role is being updated, apply default permissions for new role (unless custom permissions are being set)
  if (updates.role && !updates.permissions) {
    updateData.permissions = getDefaultPermissionsForRole(updates.role);
  }

  if (updates.isActive === true) {
    updateData.mevDeletedAt = deleteField();
  }
  if (Array.isArray(updates.stationIds) && updates.stationIds.length === 0) {
    updateData.stationIds = deleteField();
  }

  await updateDoc(userRef, updateData);

  // Replace the local list with the saved store records.
  await getPOSUsers(storeId, true);
};

const approveMevPOSUser = async (storeId: string, userId: string): Promise<void> => {
  await updateDoc(doc(db, "stores", storeId, "posUsers", userId), {
    mevVerified: true,
    mevDeletedAt: deleteField(),
    updatedAt: serverTimestamp(),
  });
};

// Certificate Add User records Revenue Quebec approval. The PIN stays on the POS user.
export const syncMevVerifiedPOSUser = async (
  storeId: string,
  name: string,
  createdBy: string
): Promise<POSUser | null> => {
  const trimmed = name.trim();
  if (!storeId || !trimmed) return null;
  const users = await removeDuplicatePOSUsers(storeId, await getPOSUsers(storeId, true));
  const existing = pickExistingPOSUser(users, trimmed);
  if (existing) {
    await approveMevPOSUser(storeId, existing.id);
    const refreshed = await getPOSUsers(storeId, true);
    return refreshed.find((user) => user.id === existing.id) || null;
  }

  const now = serverTimestamp();
  const posUserRef = doc(collection(db, "stores", storeId, "posUsers"));
  await setDoc(posUserRef, {
    name: trimmed,
    pinHash: "",
    pin: "",
    role: "cashier",
    isActive: true,
    mevVerified: true,
    createdBy: createdBy || "",
    createdAt: now,
    updatedAt: now,
  });
  const refreshed = await getPOSUsers(storeId, true);
  return refreshed.find((user) => user.id === posUserRef.id) || null;
};

// Names still registered on the certificate stay approved. PIN and active status stay as they are.
export const markListedMevUsers = async (storeId: string, names: string[]): Promise<void> => {
  if (!storeId || names.length === 0) return;
  const posUsers = await getPOSUsers(storeId, true);
  const writes: Array<Promise<void>> = [];
  names.forEach((name) => {
    const existing = pickExistingPOSUser(posUsers, name);
    if (!existing || (existing.mevVerified && !existing.mevDeletedAt)) return;
    writes.push(approveMevPOSUser(storeId, existing.id));
  });
  if (writes.length === 0) return;
  await Promise.all(writes);
  await getPOSUsers(storeId, true);
};

// Certificate Delete User keeps the POS user and records when the name was removed.
export const markPOSUserMevDeleted = async (storeId: string, name: string): Promise<void> => {
  const key = normalizePOSUserName(name);
  if (!storeId || !key) return;
  const users = await getPOSUsers(storeId, true);
  const matches = users.filter((user) => normalizePOSUserName(user.name || "") === key);
  if (matches.length === 0) return;
  const now = serverTimestamp();
  await Promise.all(matches.map((user) => updateDoc(doc(db, "stores", storeId, "posUsers", user.id), {
    mevVerified: false,
    mevDeletedAt: now,
    updatedAt: now,
  })));
  await getPOSUsers(storeId, true);
};

export const markPOSUserDeleted = async (storeId: string, userId: string): Promise<void> => {
  const now = serverTimestamp();
  await updateDoc(doc(db, "stores", storeId, "posUsers", userId), {
    mevDeletedAt: now,
    updatedAt: now,
  });
  await getPOSUsers(storeId, true);
};

// Delete POS user
export const deletePOSUser = async (storeId: string, userId: string): Promise<void> => {
  const userRef = doc(db, "stores", storeId, "posUsers", userId);
  await deleteDoc(userRef);

  // Remove from IndexedDB
  try {
    const db = await initIndexedDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    store.delete(userId);
  } catch (error) {
    console.error("Error removing POS user from IndexedDB:", error);
  }
};

// Update last login timestamp
export const updateLastLogin = async (storeId: string, userId: string): Promise<void> => {
  const userRef = doc(db, "stores", storeId, "posUsers", userId);
  await updateDoc(userRef, {
    lastLoginAt: serverTimestamp(),
  });
};

